import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

// Types for admin education management
export interface AdminEducationCategory {
  id: string;
  name: string;
  description?: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface AdminEducationModule {
  id: string;
  title: string;
  description?: string;
  content_type: string;
  content_text?: string;
  content_url?: string;
  category_id?: string;
  duration_minutes: number;
  points: number;
  is_required: boolean;
  sort_order: number;
  pass_threshold: number;
  created_at: string;
  updated_at: string;
  category?: { name: string };
}

export interface AdminModuleSlide {
  id: string;
  module_id: string;
  title: string;
  content?: string;
  slide_type: 'content' | 'image' | 'video';
  media_url?: string;
  sort_order: number;
  footnotes?: string;
  created_at: string;
}

export interface AdminQuizQuestion {
  id: string;
  module_id: string;
  question_text: string;
  question_type: 'multiple_choice' | 'true_false' | 'short_answer';
  options?: any;
  correct_answer: string;
  explanation?: string;
  points: number;
  sort_order: number;
  difficulty_level: 'easy' | 'medium' | 'hard';
  time_limit_seconds?: number;
  feedback_correct?: string;
  feedback_incorrect?: string;
  created_at: string;
}

// Categories
export const useAdminCategories = () => {
  return useQuery({
    queryKey: ['admin-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('education_categories')
        .select('*')
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data as AdminEducationCategory[];
    },
  });
};

export const useCreateCategory = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (categoryData: Omit<AdminEducationCategory, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('education_categories')
        .insert([categoryData])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
      toast({ title: "Category created successfully" });
    },
    onError: (error) => {
      toast({ 
        title: "Error creating category", 
        description: error.message,
        variant: "destructive" 
      });
    },
  });
};

export const useUpdateCategory = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<AdminEducationCategory> & { id: string }) => {
      const { data, error } = await supabase
        .from('education_categories')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
      toast({ title: "Category updated successfully" });
    },
    onError: (error) => {
      toast({ 
        title: "Error updating category", 
        description: error.message,
        variant: "destructive" 
      });
    },
  });
};

export const useDeleteCategory = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('education_categories')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
      toast({ title: "Category deleted successfully" });
    },
    onError: (error) => {
      toast({ 
        title: "Error deleting category", 
        description: error.message,
        variant: "destructive" 
      });
    },
  });
};

// Modules
export const useAdminModules = (categoryId?: string) => {
  return useQuery({
    queryKey: ['admin-modules', categoryId],
    queryFn: async () => {
      let query = supabase
        .from('education_modules')
        .select(`
          *,
          category:education_categories(name)
        `)
        .order('sort_order', { ascending: true });
      
      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as AdminEducationModule[];
    },
  });
};

export const useCreateModule = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (moduleData: Omit<AdminEducationModule, 'id' | 'created_at' | 'updated_at' | 'category'>) => {
      const { data, error } = await supabase
        .from('education_modules')
        .insert([moduleData])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-modules'] });
      toast({ title: "Module created successfully" });
    },
    onError: (error) => {
      toast({ 
        title: "Error creating module", 
        description: error.message,
        variant: "destructive" 
      });
    },
  });
};

export const useUpdateModule = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<AdminEducationModule> & { id: string }) => {
      const { data, error } = await supabase
        .from('education_modules')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-modules'] });
      toast({ title: "Module updated successfully" });
    },
    onError: (error) => {
      toast({ 
        title: "Error updating module", 
        description: error.message,
        variant: "destructive" 
      });
    },
  });
};

export const useDeleteModule = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('education_modules')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-modules'] });
      toast({ title: "Module deleted successfully" });
    },
    onError: (error) => {
      toast({ 
        title: "Error deleting module", 
        description: error.message,
        variant: "destructive" 
      });
    },
  });
};

// Slides
export const useModuleSlides = (moduleId: string) => {
  return useQuery({
    queryKey: ['module-slides', moduleId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('module_slides')
        .select('*')
        .eq('module_id', moduleId)
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data as AdminModuleSlide[];
    },
    enabled: !!moduleId,
  });
};

export const useCreateSlide = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (slideData: Omit<AdminModuleSlide, 'id' | 'created_at'>) => {
      const { data, error } = await supabase
        .from('module_slides')
        .insert([slideData])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['module-slides', data.module_id] });
      toast({ title: "Slide created successfully" });
    },
    onError: (error) => {
      toast({ 
        title: "Error creating slide", 
        description: error.message,
        variant: "destructive" 
      });
    },
  });
};

export const useUpdateSlide = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<AdminModuleSlide> & { id: string }) => {
      const { data, error } = await supabase
        .from('module_slides')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['module-slides', data.module_id] });
      toast({ title: "Slide updated successfully" });
    },
    onError: (error) => {
      toast({ 
        title: "Error updating slide", 
        description: error.message,
        variant: "destructive" 
      });
    },
  });
};

export const useDeleteSlide = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async ({ id, moduleId }: { id: string; moduleId: string }) => {
      const { error } = await supabase
        .from('module_slides')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['module-slides', variables.moduleId] });
      toast({ title: "Slide deleted successfully" });
    },
    onError: (error) => {
      toast({ 
        title: "Error deleting slide", 
        description: error.message,
        variant: "destructive" 
      });
    },
  });
};

// Quiz Questions (Admin access - includes answers)
export const useModuleQuestions = (moduleId: string) => {
  return useQuery({
    queryKey: ['module-questions', moduleId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_quiz_questions_admin', {
        module_uuid: moduleId
      });
      
      if (error) throw error;
      return data as AdminQuizQuestion[];
    },
    enabled: !!moduleId,
  });
};

export const useCreateQuestion = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (questionData: Omit<AdminQuizQuestion, 'id' | 'created_at'>) => {
      const { data, error } = await supabase
        .from('quiz_questions')
        .insert([questionData])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['module-questions', data.module_id] });
      toast({ title: "Question created successfully" });
    },
    onError: (error) => {
      toast({ 
        title: "Error creating question", 
        description: error.message,
        variant: "destructive" 
      });
    },
  });
};

export const useUpdateQuestion = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<AdminQuizQuestion> & { id: string }) => {
      const { data, error } = await supabase
        .from('quiz_questions')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['module-questions', data.module_id] });
      toast({ title: "Question updated successfully" });
    },
    onError: (error) => {
      toast({ 
        title: "Error updating question", 
        description: error.message,
        variant: "destructive" 
      });
    },
  });
};

export const useDeleteQuestion = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async ({ id, moduleId }: { id: string; moduleId: string }) => {
      const { error } = await supabase
        .from('quiz_questions')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['module-questions', variables.moduleId] });
      toast({ title: "Question deleted successfully" });
    },
    onError: (error) => {
      toast({ 
        title: "Error deleting question", 
        description: error.message,
        variant: "destructive" 
      });
    },
  });
};